import { RouterHelpers } from "@core/helpers/router.helpers";
import { Logger } from "@core/helpers/logger.helpers";
import { AuthenticationManager } from "@core/managers/authentication.manager";
import { META_FORCE_ANONYMOUS, META_ALLOW_ANONYMOUS } from "@core/constants/router.constants";

/**
 * This navigation guard takes care of the navigation triage for authenticated and not authenticated users
 * @example
 * meta: { [META_FORCE_ANONYMOUS]: true }
 * meta: { [META_ALLOW_ANONYMOUS]: true }
 * // note: { [META_FORCE_ANONYMOUS]: true } prevails over { [META_ALLOW_ANONYMOUS]: true }, no need to put both
 */
export function setupAuthenticationGuard({ authHomeRoute, loginRoute } = {}) {
  /**
   * return Vue-Router NavigationGuard
   */
  return async (to, from, next) => {
    try {
      const userIsAuthenticated = await AuthenticationManager.isUserAuthenticated();
      const routeForceAnonymous = RouterHelpers.routeAncestorsMatchMeta(to, META_FORCE_ANONYMOUS);
      const routeAllowsAnonymous = routeForceAnonymous || RouterHelpers.routeAncestorsMatchMeta(to, META_ALLOW_ANONYMOUS);

      if (userIsAuthenticated) {
        // if the user is auth but the route forbid access, we go to app home
        if (routeForceAnonymous) {
          Logger.info(`authenticationGuard - auth user is not allowed here: ${to.name}, going to home`);
          next({ name: authHomeRoute });
        } else {
          // otherwise proceed to the desired route
          next();
        }
      }

      // if the user is not auth and the route is marked as anonymous, proceed
      else if (routeAllowsAnonymous) {
        next();
      } else {
        // if the route requires authentication
        // but the user is not authenticated
        // let's go to login page and keep the previous route as a redirect rule
        Logger.info(`authenticationGuard - not auth user is not allowed here: ${to.name}, going to login`);
        next({ name: loginRoute, query: { redirect: to.fullPath } });
      }
    } catch (error) {
      Logger.warn("authenticationGuard failure, going to login", error);
      // if anything happens badly, let's go to login
      next({ name: loginRoute });
    }
  };
}
